/**
 * Google Ads conversion reporting.
 *
 * Reports ONE conversion — "Website Lead - Enquiry Confirmed" — and only after
 * the server has confirmed it actually captured the enquiry. This replaces
 * counting a thank-you page load as a lead.
 *
 * A tap on a phone button is deliberately NOT reported: a tap is not a lead.
 * Real calls are measured by Google's own call tracking.
 *
 * Consent route: no banner, so nothing loads or sends in regions that require
 * prior consent (see adsConsent.ts).
 */

import { canTrackAds } from "./adsConsent";

const ADS_ID = "AW-400610291";
const LEAD_SEND_TO = "AW-400610291/on5rCO7W-vgcEPOng78B";
const WEBSITE_CALL_SEND_TO = "AW-400610291/g5oXCK2JyLgDEPOng78B";
const SITE_CALL_NUMBER = "6474921989";

const CONFIRMED_KEY = "tron_ads_confirmed_leads";
const REPORTED_KEY = "tron_ads_reported_leads";
const MAX_IDS = 25;

// Window.dataLayer / Window.gtag are declared in ./analytics.

function readIds(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const list = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(list) ? list.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function writeIds(key: string, list: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(list.slice(-MAX_IDS)));
  } catch {
    /* storage unavailable — nothing to report */
  }
}

const AWAITING_KEY = "tron_ads_awaiting_lead";

/**
 * Called the moment a form is submitted, before the browser posts it.
 * The enquiry only counts once the server has accepted it — which is proven by
 * the visitor landing on /thank-you, a page only the server redirects to.
 */
export function markLeadAwaiting(submissionId: string): void {
  if (!submissionId) return;
  try {
    localStorage.setItem(AWAITING_KEY, submissionId);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Called on /thank-you: arriving here means the server processed the enquiry,
 * so the awaiting submission becomes a confirmed, reportable lead.
 */
export function confirmAwaitingLead(): void {
  try {
    const id = localStorage.getItem(AWAITING_KEY);
    if (!id) return;
    localStorage.removeItem(AWAITING_KEY);
    if (['skipped', 'failed'].includes(new URLSearchParams(window.location.search).get('lead_status') || '')) return;
    markLeadConfirmed(id);
  } catch {
    /* storage unavailable */
  }
}

/** Called when the server has confirmed it recorded this enquiry. */
export function markLeadConfirmed(submissionId: string): void {
  if (!submissionId) return;
  if (readIds(REPORTED_KEY).includes(submissionId)) return;
  const list = readIds(CONFIRMED_KEY).filter((id) => id !== submissionId);
  list.push(submissionId);
  writeIds(CONFIRMED_KEY, list);
}

export async function loadGoogleAds(): Promise<boolean> {
  if (!(await canTrackAds())) return false;
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function (...args: any[]) {
      window.dataLayer!.push(args);
    };
  // ad_user_data / ad_personalization come from the regional defaults in
  // index.html (granted outside consent regions, denied in EEA/UK/CH/Quebec).
  // Not set here: a country-level check cannot see Quebec, so an update
  // would override Quebec's stricter default.
  window.gtag("consent", "update", {
    ad_storage: "granted",
  });
  if (document.querySelector("script[data-google-ads]")) return true;
  window.gtag("js", new Date());
  window.gtag("config", ADS_ID);
  // Website call tracking: Google swaps this number on the page for a
  // forwarding number so calls from ad visitors are counted.
  window.gtag("config", WEBSITE_CALL_SEND_TO, { phone_conversion_number: SITE_CALL_NUMBER });
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
  script.async = true;
  script.dataset.googleAds = "";
  document.head.appendChild(script);
  return true;
}

/**
 * Reports every server-confirmed enquiry that has not been reported yet.
 * The submission id travels as transaction_id, so a reload can never
 * double-count the same enquiry.
 */
export async function reportConfirmedLeads(): Promise<void> {
  const pending = readIds(CONFIRMED_KEY);
  if (pending.length === 0) return;
  if (!(await loadGoogleAds())) return;

  const reported = readIds(REPORTED_KEY);
  for (const id of pending) {
    if (reported.includes(id)) continue;
    window.gtag?.("event", "conversion", {
      send_to: LEAD_SEND_TO,
      value: 1.0,
      currency: "CAD",
      transaction_id: id,
    });
    reported.push(id);
  }
  writeIds(REPORTED_KEY, reported);
  writeIds(CONFIRMED_KEY, []);
}
